import React from 'react';
import { useTranslation } from 'react-i18next';
import { Layers } from 'lucide-react';
import { ECO_GROUPS, SUITE_META } from '@/data/landing2Data';
import { SuiteGlyph } from './SuiteVisuals';

// Desktop composition (Founder remediation §5): g1 full row (3 cols),
// 2-suite groups paired side by side, g6 as a centered closing block.
const ROWS = [['g1'], ['g2', 'g3'], ['g4', 'g5'], ['g6']];
const GROUPS = Object.fromEntries(ECO_GROUPS.map((g) => [g.key, g]));

// Global card numbering (01–13) derived once from the group order.
const CARD_NUM = (() => {
  const m = {};
  let n = 0;
  ECO_GROUPS.forEach((g) => g.ids.forEach((sid) => { n += 1; m[sid] = String(n).padStart(2, '0'); }));
  return m;
})();

const SuiteCard = ({ sid, t }) => {
  const meta = SUITE_META[sid];
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col"
      style={{ borderTop: `3px solid ${meta.color}` }}
      data-testid={`eco-card-${sid}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${meta.color}12` }}>
          <SuiteGlyph sid={sid} />
        </div>
        <span className="text-xs font-bold tabular-nums" style={{ color: meta.color }}>{CARD_NUM[sid]}</span>
      </div>
      <div className="font-bold text-sm">{meta.name}</div>
      <div className="text-xs font-medium mt-0.5" style={{ color: meta.color }}>{t(`pages.landing2.eco.suites.${sid}.tag`)}</div>
      <p className="text-xs text-slate-500 mt-2 leading-relaxed">{t(`pages.landing2.eco.suites.${sid}.desc`)}</p>
    </div>
  );
};

const GroupBlock = ({ gkey, t, centered }) => {
  const g = GROUPS[gkey];
  const cols = g.ids.length >= 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2';
  return (
    <div className={centered ? 'lg:max-w-3xl lg:mx-auto w-full' : ''} data-testid={`eco-group-${gkey}`}>
      <p className={`text-xs uppercase tracking-[0.25em] font-bold text-slate-400 mb-3 ${centered ? 'lg:text-center' : ''}`}>
        {t(`pages.landing2.eco.${gkey}`)}
      </p>
      <div className={`grid ${cols} gap-5`}>
        {g.ids.map((sid) => <SuiteCard key={sid} sid={sid} t={t} />)}
      </div>
    </div>
  );
};

export default function EcosystemSection() {
  const { t } = useTranslation();
  return (
    <section id="ecosistema" className="bg-white" data-testid="ecosystem-section">
      <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="max-w-3xl mb-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-1.5 text-xs font-semibold text-[#176A70]">
            <Layers className="h-3.5 w-3.5" /> {t('pages.landing2.eco.eyebrow')}
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight">{t('pages.landing2.eco.heading')}</h2>
          <p className="mt-4 text-slate-600 md:text-lg">{t('pages.landing2.eco.sub')}</p>
        </div>

        <div className="space-y-8 lg:space-y-9">
          {ROWS.map((row, i) => (
            row.length === 2 ? (
              <div key={i} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
                {row.map((k) => <GroupBlock key={k} gkey={k} t={t} />)}
              </div>
            ) : (
              <GroupBlock key={row[0]} gkey={row[0]} t={t} centered={row[0] === 'g6'} />
            )
          ))}
        </div>
      </div>
    </section>
  );
}
